import React, { useState, useRef, useEffect } from 'react';
import "./Photo.css";
import { shrinkDataUrl } from './imageCompress';

// inline: the panel sits in the page flow and pushes whatever is below it down,
// instead of floating over it. Used on the final submit screen, where a floating
// panel covers the "Generate PDF and submit" button and a slow-Wi-Fi tap lands on it.
// onBusyChange: told true while newly picked photos are still being processed.
const PhotoUpload = ({ itemKey, photos = [], onPhotoChange, maxPhotos = 4, inline = false, onBusyChange }) => {
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(false);
  const panelRef = useRef(null);

  // The floating panel opens below its button, which can be under the bottom of the
  // screen on the last rows. Scroll just far enough to bring all of it into view,
  // again when photos are added since the panel grows.
  useEffect(() => {
    if (expanded && !inline) {
      panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [expanded, inline, photos.length]);

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      if (!(file instanceof Blob)) { reject(new Error("Not a valid file")); return; }
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });

  const handleFileInput = async (e) => {
    const newFiles = Array.from(e.target.files);
    e.target.value = '';
    if (!newFiles.length) return;
    setBusy(true);
    onBusyChange?.(true);
    try {
      const converted = await Promise.all(newFiles.map(async f => shrinkDataUrl(await toBase64(f))));
      const combined = [...photos, ...converted].slice(0, maxPhotos);
      onPhotoChange(itemKey, combined);
    } finally {
      setBusy(false);
      onBusyChange?.(false);
    }
  };

  const handleRemove = (index) => {
    onPhotoChange(itemKey, photos.filter((_, i) => i !== index));
  };

  const allFull = photos.length >= maxPhotos;
  const hasPhotos = photos.length > 0;

  return (
    <div className={`pu-root ${inline ? 'inline' : ''}`}>

      {/* ── Trigger button ── */}
      <button
        className={`pu-trigger ${hasPhotos ? 'has-photos' : ''} ${allFull ? 'full' : ''}`}
        onClick={() => setExpanded(v => !v)}
        title={expanded ? 'Collapse' : 'Expand photos'}
      >
        <span className="pu-icon">📷</span>
        <span className="pu-count">
          <span className="pu-count-num">{photos.length}</span>
          <span className="pu-count-sep">/</span>
          <span className="pu-count-max">{maxPhotos}</span>
        </span>
        <span className={`pu-chevron ${expanded ? 'open' : ''}`}>▾</span>
      </button>

      {/* ── Expanded panel ── */}
      {expanded && (
        <div ref={panelRef} className={`pu-panel ${inline ? 'inline' : ''}`}>

          {/* Upload button */}
          {!allFull && (
            <label className="pu-upload-btn" htmlFor={`photo-input-${itemKey}`}>
              <span>{busy ? 'Adding photo…' : '+ Add photo'}</span>
              <input
                id={`photo-input-${itemKey}`}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                disabled={busy}
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </label>
          )}

          {/* Thumbnails */}
          {photos.length > 0 ? (
            <div className="pu-grid">
              {photos.map((src, i) => (
                <div key={i} className="pu-thumb-wrap">
                  <img src={src} alt={`Photo ${i + 1}`} className="pu-thumb" />
                  <button
                    className="pu-remove"
                    onClick={() => handleRemove(i)}
                    title="Remove"
                  >×</button>
                </div>
              ))}
            </div>
          ) : (
            <p className="pu-empty">No photos yet</p>
          )}

        </div>
      )}

    </div>
  );
};

export default PhotoUpload;